/**
 * RESPONSIBILITY: draw the contact-requests page - a waiting list and an archive.
 *
 * One page, two tabs, each with its count, so the admin sees at a glance whether anything
 * is waiting. THE TABS ARE ADDRESSES (#requests and #requests/archive), not hidden state:
 * the archive can be bookmarked, linked to, and reloaded, and is never more than one
 * click from the list. Nothing here deletes anything - Done archives, Redo un-archives,
 * and that is all either does.
 */
import { hrefFor, Page } from '../commons/pages';
import type { RequestsSection } from '@app/sections';
import type { ContactRequest } from './useRequests';
import { RequestCard } from './RequestCard';

export const ARCHIVE = 'archive';

export interface RequestsViewProps {
  c: RequestsSection;
  rows: readonly ContactRequest[] | undefined;
  failed: boolean;
  /** Which tab the address names. */
  archive: boolean;
  onDone: (id: number, done: boolean) => void;
  onNote: (id: number, note: string) => Promise<unknown>;
}

export function RequestsView({ c, rows, failed, archive: showArchive, onDone, onNote }: RequestsViewProps) {
  const all = rows ?? [];
  const queue = all.filter((r) => r.handledAt === null);
  const archive = all.filter((r) => r.handledAt !== null);
  const shown = showArchive ? archive : queue;

  return (
    <section className="band">
      <div className="wrap rq-wrap">
        <h1>{c.heading}</h1>

        <nav className="rq-tabs" aria-label={c.heading}>
          <a href={hrefFor(Page.Requests)} aria-current={showArchive ? undefined : 'page'} className={showArchive ? '' : 'is-on'}>
            {c.queueLabel} <span className="rq-count">{queue.length}</span>
          </a>
          <a href={hrefFor(Page.Requests, ARCHIVE)} aria-current={showArchive ? 'page' : undefined} className={showArchive ? 'is-on' : ''}>
            {c.archiveLabel} <span className="rq-count">{archive.length}</span>
          </a>
        </nav>

        {failed ? <p className="rq-empty">{c.failedMessage}</p> : null}
        {!failed && rows && !shown.length ? (
          <p className="rq-empty">{showArchive ? c.emptyArchive : c.emptyQueue}</p>
        ) : null}

        <div className="rq-list">
          {shown.map((r) => <RequestCard key={r.id} c={c} r={r} onDone={onDone} onNote={onNote} />)}
        </div>
      </div>
    </section>
  );
}
